import { Injectable, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, fromEvent, map, merge, Observable, shareReplay, Subscription, tap, withLatestFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ConnectionStatusService implements OnDestroy {
  private offlineMode_$ = new BehaviorSubject<boolean>(localStorage.getItem("forcedOfflineMode") === "true");
  offlineMode$ = this.offlineMode_$.pipe(
    tap(state => localStorage.setItem("forcedOfflineMode", String(state)))
  );

  networkStatus$ = merge(
    this.offlineMode$,
    fromEvent(window, 'online'),
    fromEvent(window, 'offline')
  )
  .pipe(
    withLatestFrom(this.offlineMode$),
    map(([_, offlineMode]) => !offlineMode && navigator.onLine),
    distinctUntilChanged(),
    tap(this.showNetworkStatus()),
    shareReplay({refCount: true, bufferSize: 1})
  )

  private syncStatus_$ = new BehaviorSubject<boolean>(false);
  private wakeLockSentinel: WakeLockSentinel | null = null;
  private readonly syncSubscription = new Subscription();

  public get syncStatus$(): Observable<boolean> {
    return this.syncStatus_$.asObservable();
  }

  isSyncing(): boolean {
    return this.syncStatus_$.value;
  }

  constructor(
    private _snackBar: MatSnackBar,
    private _translate: TranslateService,
  ) {
    this.networkStatus$.subscribe();
    this.syncSubscription.add(
      this.syncStatus$.subscribe(isSyncing => {
        if (isSyncing) {
          void this.acquireWakeLock();
        } else {
          void this.releaseWakeLock();
        }
      })
    );
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  ngOnDestroy(): void {
    this.syncSubscription.unsubscribe();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    void this.releaseWakeLock();
  }

  isOnline(): boolean {
    return !this.offlineMode_$.value && navigator.onLine;
  }

  goOffline(){
    this.offlineMode_$.next(true);
  }

  goOnline() {
    this.offlineMode_$.next(false);
  }

  showNetworkStatus(): (status: boolean) => void {
    let lastStatus = true;
    return (status) => {
      if(status === lastStatus){
        return;
      }
      lastStatus = status;
      setTimeout(() => {
        const key = "NETWORK." + (status ? "ON" : "OFF");
        const msg = this._translate.instant(key);
        this._snackBar.open(msg, "OK", {
          duration: 3000,
          panelClass: ['network-snackbar']
        });
      }, 1);
    }
  }

  setSyncStatus(status: boolean) {
    this.syncStatus_$.next(status);
  }

  private readonly handleVisibilityChange = (): void => {
    if (!this.syncStatus_$.value) {
      return;
    }

    if (document.visibilityState === 'visible') {
      void this.acquireWakeLock();
      return;
    }

    void this.releaseWakeLock();
  };

  private async acquireWakeLock(): Promise<void> {
    if (!('wakeLock' in navigator) || document.visibilityState !== 'visible' || this.wakeLockSentinel) {
      return;
    }

    try {
      this.wakeLockSentinel = await navigator.wakeLock.request('screen');
      this.wakeLockSentinel.addEventListener('release', this.handleWakeLockRelease, { once: true });
    } catch {
      this.wakeLockSentinel = null;
    }
  }

  private readonly handleWakeLockRelease = (): void => {
    this.wakeLockSentinel = null;
    if (this.syncStatus_$.value && document.visibilityState === 'visible') {
      void this.acquireWakeLock();
    }
  };

  private async releaseWakeLock(): Promise<void> {
    if (!this.wakeLockSentinel) {
      return;
    }

    const sentinel = this.wakeLockSentinel;
    this.wakeLockSentinel = null;

    try {
      await sentinel.release();
    } catch {
      // Wake lock is best effort; release failures can be safely ignored.
    }
  }
}
